import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { concatMap, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AdminDashboardService, DashboardService, UserDashboardService } from 'src/app/services/api.services';
import { SnackService } from 'src/app/services/snack.service';
import { getDeleteDialogData } from 'src/app/shared/utils/deleteMessage';
import { DashboardsActions, DashboardsSelectors } from '.';
import { GenericAlertDialog } from '../../../shared/dialogs';
import { catchAppError } from '../../../shared/utils';
import { AppSelectors } from '../../../state/app';
import { DashboardsCategory, DashboardsDashboard } from './reducer';

@Injectable()
export class DashboardsEffects
{
  constructor(
    private store: Store,
    private actions$: Actions,
    private router: Router,
    private snackService: SnackService,
    private dialog: MatDialog,
    private dashboardService: DashboardService,
    private userDashboardService: UserDashboardService,
    private adminDashboardService: AdminDashboardService) { }

  getDashboards$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.getDashboards),
    withLatestFrom(this.store.select(AppSelectors.getUser)),
    switchMap(([{ }, user]) => this.dashboardService.listDashboards(user.roles.isAdmin).pipe(
      switchMap((dashboards) => {
        if (user.roles.isAdmin) {
          // For admins, load welcome dashboard state as well
          return this.adminDashboardService.listWelcomeDashboards(0).pipe(
            map(welcomeDashboards => {
              let welcomeDashboardIds = welcomeDashboards.reduce((s, w) => s.add(w.id), new Set<number>());

              return DashboardsActions.getDashboardsSuccess({
                dashboards: dashboards.map(d => <DashboardsDashboard>{
                  ...d,
                  onWelcomeScreen: welcomeDashboardIds.has(d.id)
                })
              });
            }),
            catchAppError(error => {
              return of(DashboardsActions.getDashboardsFailure({ error }));
            })
          );
        }
        else {
          return of(DashboardsActions.getDashboardsSuccess({ dashboards }));
        }
      }),
      catchAppError(error => {
        return of(DashboardsActions.getDashboardsFailure({ error }));
      })
    )))
  );

  
  addDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.addDashboard),
    tap(() => {
      this.router.navigate(['dashboards', 'add']);
    })),
    { dispatch: false }
  );

  editDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.editDashboard),
    withLatestFrom(this.store.select(DashboardsSelectors.getSelectedDashboardId)),
    tap(([{ dashboardId }, selectedDashboardId]) => {
      dashboardId ??= selectedDashboardId;

      this.router.navigate(['dashboards', dashboardId, 'edit']);
    })),
    { dispatch: false }
  );

  viewDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.viewDashboard),
    withLatestFrom(this.store.select(DashboardsSelectors.getSelectedDashboardId)),
    tap(([{ dashboardId }, selectedDashboardId]) => {
      dashboardId ??= selectedDashboardId;

      this.router.navigate(['dashboards', dashboardId]);
    })),
    { dispatch: false }
  );

  addScheduleDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.addScheduleDashboard),
    withLatestFrom(this.store.select(DashboardsSelectors.getSelectedDashboardId)),
    tap(([{ dashboardId }, selectedDashboardId]) => { 
      dashboardId ??= selectedDashboardId;
      this.router.navigate(['scheduler/add/dashboard', dashboardId], {queryParams: {returnUrl: '/dashboards'}})
    })),
    { dispatch: false }
  );

  copyDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.copyDashboard),
    withLatestFrom(this.store.select(DashboardsSelectors.getSelectedDashboardId)),
    switchMap(([{ dashboardId }, selectedDashboardId]) => {
      dashboardId ??= selectedDashboardId;

      return this.dashboardService.copyDashboard(dashboardId).pipe(
        map((dashboard) => DashboardsActions.copyDashboardSuccess({ dashboard })),
        catchAppError(error => {
          return of(DashboardsActions.copyDashboardFailure({ error }));
        })
      );
    }))
  );

  selectAfterCopyDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.copyDashboardSuccess),
    mergeMap(({ dashboard }) => [
      DashboardsActions.setSelectedDashboardId({ dashboardId: dashboard.id }),
      DashboardsActions.updateDisplayParams({ displayParams: { category: DashboardsCategory.User } })
    ]))
  );

  // Navigate to edit view of the copy dashboard
  navigateAfterCopyDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.copyDashboardSuccess),
    map(({ dashboard }) => DashboardsActions.editDashboard({ dashboardId: dashboard.id })))
  );
  

  showDeleteDashboardDialog$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.showDeleteDashboardDialog),
    withLatestFrom(
      this.store.select(DashboardsSelectors.getSelectedDashboardId),
      this.store.select(DashboardsSelectors.getDashboards)),
    switchMap(([{ dashboardId }, selectedDashboardId, dashboards]) => {
      dashboardId ??= selectedDashboardId;

      const dashboard = dashboards.find(d => d.id === dashboardId);

      return this.dashboardService.getUsage(dashboardId).pipe(
        tap(usage => {
          let dialogData = getDeleteDialogData('Dashboard', dashboard.name, {
            shared: dashboard.shared,
            scheduled: usage.inSchedule,
            published: usage.published,
            wrapNameInStrongTag: true
          });

          let dialogRef = this.dialog.open(GenericAlertDialog, {
            width: '560px',
            data: dialogData
          });

          return dialogRef.afterClosed().pipe(
            tap((result) => {
              if (!result) {
                return;
              }

              this.store.dispatch(DashboardsActions.deleteDashboard({ dashboardId }));
            })
          ).subscribe();
        })
      );
    })),
    { dispatch: false }
  );

  deleteDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.deleteDashboard),
    withLatestFrom(this.store.select(DashboardsSelectors.getSelectedDashboardId)),
    switchMap(([{ dashboardId }, selectedDashboardId]) => {
      dashboardId ??= selectedDashboardId;

      return this.dashboardService.markAsDeleted(dashboardId).pipe(
        map(() => DashboardsActions.deleteDashboardSuccess({ dashboardId })),
        catchAppError(error => {
          return of(DashboardsActions.deleteDashboardFailure({ error }));
        })
      );
    }))
  );

  /*
  shareDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.shareDashboard),
    withLatestFrom(this.store.select(DashboardsSelectors.getSelectedDashboardId)),
    switchMap(([{ dashboardId }, selectedDashboardId]) => {
      dashboardId ??= selectedDashboardId;

    }))
  );
  */

  updateUserDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.updateUserDashboard),
    concatMap(({ dashboard }) => {
      let userDashboard = { 
        dashboardId: dashboard.id, 
        favourite: dashboard.favourite, 
        home: dashboard.home
      };

      return this.userDashboardService.updateUserDashboard(userDashboard).pipe(
        map(() => DashboardsActions.updateUserDashboardSuccess( { userDashboard })),
        catchAppError(error => {
          return of(DashboardsActions.updateUserDashboardFailure({ error }));
        })
      )})    
  ));   
 

  toggleDashboardLibrary$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.toggleDashboardLibrary),
    switchMap(({ dashboard }) => {
      return this.dashboardService.setFromLibrary(dashboard.id, !dashboard.fromLibrary).pipe(
        map((fromLibrary) => DashboardsActions.toggleDashboardLibrarySuccess({ dashboardId: dashboard.id, fromLibrary })),
        catchAppError(error => {
          return of(DashboardsActions.toggleDashboardLibraryFailure({ error }))
        })
    );
   }))
  );

  toggleDashboardOnWelcomeScreen$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardsActions.toggleDashboardOnWelcomeScreen),
    switchMap(({ dashboard }) => {
      let onWelcomeScreen = !dashboard.onWelcomeScreen;

      let organisationIds = [];

      if (onWelcomeScreen) {
        organisationIds.push(null);
      }

      return this.adminDashboardService.setDashboardWelcomeScreens({
        dashboardId: dashboard.id,
        organisationIds
      }).pipe(
        map((fromLibrary) => DashboardsActions.toggleDashboardOnWelcomeScreenSuccess({ dashboardId: dashboard.id, onWelcomeScreen })),
        catchAppError(error => {
          return of(DashboardsActions.toggleDashboardOnWelcomeScreenFailure({ error }))
        })
      );
    }))
  );
}
